import { createAction, props } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';

import {
  AirBookingPayload, HotelBookingPayload, PackageBookingPayload, AutoBookingPayload
} from '@booking/entities/booking-arrangement';
import {
  AirValidationBySectorResponse,
  AirValidationResponse,
  AirValidationResponseError,
} from '@app/entities/air-validation.response';
import { User } from '@app/modules/core/interfaces/user';
import { type } from '@app/util/util';
import { VehicleValidatePayload } from '@autos/entities/vehicle-validate.payload';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { AirValidationBySectorPayload, AirValidationPayload } from '@booking/entities/air-validation.payload';
import { HotelValidationPayload, ByHoursData } from '@booking/entities/hotel-validation.payload';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { PackageValidationPayload } from '@booking/entities/package-validation.payload';
import { PackageValidationResponse } from '@booking/entities/package-validation.response';
import { RappiPayment } from '@booking/entities/rappi-payment';
import { RappiPaymentResponse } from '@booking/entities/rappi-payment.response';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import {
  ActivitiesBookPayloadVM, ActivitiesValidatePayload, ActivitiesValidateResponse
} from '@activities/entities/activities-booking';

export const fetchAirValidationAction = createAction(
  type('[Booking/API] Fetch air validation'),
  props<{ payload: AirValidationPayload, user: User, isBuses: boolean }>()
);
export const fetchAirValidationSuccessAction = createAction(
  type('[Booking/API] Fetch air validation success'),
  props<{ response: AirValidationResponse, isBuses: boolean }>()
);
export const fetchAirValidationErrorAction = createAction(
  type('[Booking/API] Fetch air validation error'),
  props<{ typeError: AirValidationResponseError }>()
);
export const fetchAirValidationBySectorAction = createAction(
  type('[Booking/API] Fetch air validation by sector'),
  props<{ payload: AirValidationBySectorPayload, user: User }>()
);
export const fetchAirValidationBySectorSuccessAction = createAction(
  type('[Booking/API] Fetch air validation by sector success'),
  props<{ response: AirValidationBySectorResponse}>()
);
export const setBookingAirArrangementAction = createAction(
  type('[Booking/Data] Set air booking'),
  props<{ payload: AirBookingPayload }>()
);
export const setBookingAirBySectorArrangementAction = createAction(
  type('[Booking/Data] Set air by sector booking'),
  props<{ payload: AirBookingPayload }>()
);
export const setBookingHotelArrangementAction = createAction(
  type('[Booking/Data] Set hotel booking'),
  props<{ payload: HotelBookingPayload, searchBy: SearchBy }>()
);
export const setBookingAutoArrangementAction = createAction(
  type('[Booking/Data] Set auto booking'),
  props<{ payload: AutoBookingPayload }>()
);
export const bookingErrorAction = createAction(
  type('[Booking/Data] Booking error'),
  props<{ payload: HttpErrorResponse }>()
);
export const fetchHotelValidationAction = createAction(
  type('[Booking/API] Fetch hotel validation'),
  props<{ payload: HotelValidationPayload, user: User, byHoursData: ByHoursData }>()
);
export const fetchHotelValidationSuccessAction = createAction(
  type('[Booking/API] Fetch hotel validation success'),
  props<{ response: HotelValidationResponse }>()
);
export const fetchAutoValidationAction = createAction(
  type('[Booking/API] Fetch auto validation'),
  props<{ payload: VehicleValidatePayload, user: User }>()
);
export const fetchAutoValidationSuccessAction = createAction(
  type('[Booking/API] Fetch auto validation success'),
  props<{ response: VehicleValidateResponse }>()
);
export const fetchRappiPaymentAction = createAction(
  type('[Booking/API] Fetch Rappi payment'),
  props<{ payload: RappiPayment, searchBy: SearchBy }>()
);
export const fetchRappiPaymentDesktopAction = createAction(
  type('[Booking/API] Fetch Rappi payment desktop'),
  props<{ payload: RappiPayment, searchBy: SearchBy }>()
);
export const fetchRappiPaymentSuccessAction = createAction(
  type('[Booking/API] Fetch Rappi payment success'),
  props<{ response: RappiPaymentResponse }>()
);
export const fetchRappiIntermediaryPaymentDesktopAction = createAction(
  type('[Booking/API] Fetch Rappi intermediary payment desktop'),
  props<{ response: RappiPaymentResponse }>()
);
export const fetchCountriesAction = createAction(
  type('[Booking/API] Fetch countries')
);
export const fetchCountriesSuccessAction = createAction(
  type('[Booking/API] Fetch countries success'),
  props<{ response: string[] }>()
);
export const resetAllBookValidationErrorAction = createAction(
  type('[Booking/API] Reset all validation error')
);
export const fetchFrequentFlyerProgramsAction = createAction(
  type('[Booking/API] Fetch frequent flyer programs')
);
export const paxSectionReadyControlAction = createAction(
  type('[Booking/UI] Set pax section ready control'),
  props<{ payload: boolean }>()
);
export const bookingReadyControlAction = createAction(
  type('[Booking/UI] Set booking ready control'),
  props<{ payload: boolean }>()
);
export const resetValidatorsAction = createAction(
  type('[Booking/UI] Reset validators')
);
export const bookingChurningExcededAction = createAction(
  type('[Booking/UI] Booking churning exceded error'),
  props<{ payload: boolean }>()
);
export const fetchPackageValidationAction = createAction(
  type('[Booking/API] Fetch package validation'),
  props<{ payload: PackageValidationPayload, user: User }>()
);
export const fetchPackageValidationSuccessAction = createAction(
  type('[Booking/API] Fetch package validation success'),
  props<{ response: PackageValidationResponse }>()
);
export const setBookingPackageArrangementAction = createAction(
  type('[Booking/API] Set package booking'),
  props<{ payload: PackageBookingPayload }>()
);
export const setBookingActivitiesArrangementAction = createAction(
  type('[Booking/API] Set activities booking'),
  props<{ payload: ActivitiesBookPayloadVM }>()
);
export const setExchangeRateAction = createAction(
  type('[Booking/UI] Set booking exchange rate'),
  props<{ rate: number }>()
);
export const fetchTermsAndConditionsAction = createAction(
  type('[Booking/API] Fetch terms and conditions')
);
export const fetchTermsAndConditionsSuccessAction = createAction(
  type('[Booking/API] Fetch terms and conditions success')
);
export const proceedToCheckoutAction = createAction(
  type('[Booking/API] Checkout was opened success')
);
export const fetchActivitiesValidateAction = createAction(
  type('[Booking/API] Fetch activities validate'),
  props<{ payload: ActivitiesValidatePayload, user: User }>()
);
export const fetchActivitiesValidateSuccessAction = createAction(
  type('[Booking/API] Fetch activities validate success'),
  props<{ response: ActivitiesValidateResponse }>()
);
